import * as React from 'react';
import { Store } from '../../models/store';
import { getStore, upsertStore, getOrganization } from '../../utilities/api-utils';
import store, { useAppSelector } from '../../store';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Box,
  Button,
  DialogContent, 
  DialogTitle,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  FormHelperText
} from '@mui/material';
import { useState, useEffect } from 'react';
import timeZones from '../../assets/tzdatabase.json';

interface StoreDialogProps {
  editingStore: string,
  setDialog(arg: boolean): void,
  refresh(): void
}

function StoreDialog({ editingStore, setDialog, refresh }: StoreDialogProps) {
  const [storeName, setStoreName] = useState<string>('');
  const [storeId, setStoreId] = useState<string>('');
  const [hideId, setHideId] = useState<boolean>(false);

  const [storeAddress, setStoreAddress] = useState<string>('');
  const [storeAddress2, setStoreAddress2] = useState<string>('');
  const [storeCity, setStoreCity] = useState<string>('');
  const [storeState, setStoreState] = useState<string>('');
  const [storePostalCode, setStorePostalCode] = useState<string>('');
  const [storeCountry, setStoreCountry] = useState<string>('');

  const [storeLocale, setStoreLocale] = useState<string>('');
  const [storeProperties, setStoreProperties] = useState<Record<string, unknown>>({ enable_order_creation_from_ui_app: false });
  const [storeTimezone, setStoreTimezone] = useState<string>('');
  const [oloStoreNumber, setOloStoreNumber] = useState<string>('');
  const [enableOlo, setEnableOlo] = useState<boolean>(false);
  const [brandServices] = useState<string>('uber,doordash,allset,bitesquad,deliverclub,delivery.com,eatstreet,favor,ritual,waitr,zifty,postmates,habitat,ally,drivekindness,google');
  const { activeOrganization } = useAppSelector(store => store.organization);
  const [oloBrandDetails, setOloBrandDetails] = useState<string>(`{
    'deliveryServiceSlugsCsv': ${brandServices}},
    'orderingProviderSlugsCsv': ${brandServices}
  }`);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [storeNameErrorText, setStoreNameErrorText] = useState<string>('');
  const [localeErrorText, setLocaleErrorText] = useState<string>('');
  const [timezoneErrorText, setTimezoneErrorText] = useState<string>('');
  const [oloStoreNumberErrorText, setOloStoreNumberErrorText] = useState<string>('');
  const [hasStoreNameError, setHasStoreNameError] = useState<boolean>(false);
  const [hasLocaleError, setHasLocaleError] = useState<boolean>(false);
  const [hasTimezoneError, setHasTimezoneError] = useState<boolean>(false);
  const [hasOloStoreNumberError, setHasOloStoreNumberError] = useState<boolean>(false);
  const [enableOrderBoard, setEnableOrderBoard] = useState<boolean>(false);
  
  const toggleOlo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableOlo(event.target.checked);

    if (!enableOlo && !oloStoreNumber) {
      setIsFormValid(false);
    } else {
      setOloStoreNumberErrorText('');
      setHasOloStoreNumberError(false);
    }
  };

  const validateOloStoreNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOloStoreNumber(event.target.value);

    if (storeLocale.length && storeName.length && storeTimezone.length && storeCity.length) {
      setIsFormValid(true);
    }

    if (!event.target.value) {
      setIsFormValid(false);
    }
  };

  const upsertEditedStore = async () => {
    // convert the text field string back into valid JSON
    const oloBrandDet = JSON.parse(oloBrandDetails);
    storeProperties.has_order_board = enableOrderBoard;

    let payload = {
      name: storeName,
      address: storeAddress,
      address2: storeAddress2,
      city: storeCity,
      state: storeState,
      postal_code: storePostalCode,
      country: storeCountry,
      locale: storeLocale,
      timezone: storeTimezone,
      properties: storeProperties,
      organization_id: activeOrganization,
      olo_brand_details: oloBrandDet
    };
    
    if (storeId) {
      payload = Object.assign(payload, {
        id: storeId
      });
    }

    if (enableOlo) {
      payload = Object.assign(payload, {
        olo_store_number: oloStoreNumber
      });
    } else {
      payload = Object.assign(payload, {
        olo_store_number: null
      });
    }

    const response = await upsertStore(store.getState().authorization.cloudKey, payload);

    if (response?.status !== 201) {
      let detailString = '';
      Object.keys(response.response.data.details).forEach(k => {
        detailString += response.response.data.details[k];
      });
      alert(`${response.response.data.error}:\n${detailString}`);
    } else {
      refresh();
      setDialog(false);
    }
  };

  const validate = () => {
    const invalidDetails = !storeName || !storeLocale || !storeTimezone || !storeCity;
    if (invalidDetails) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  const setDefaultOloBrandDetails = async () => {
    // Get active organization olo_brand_details
    const thisOrg = await getOrganization(store.getState().authorization.cloudKey, activeOrganization);
    const oloBrandDet = JSON.stringify(thisOrg.data[0].olo_brand_details).replace('","', '",\r\n"');
    setOloBrandDetails(oloBrandDet);
  };

  useEffect(() => {
    validate();
  }, [storeName, storeLocale, storeTimezone, storeCity]);

  useEffect(() => {
    const fetchStore = async () => {
      const response = await getStore(store.getState().authorization.cloudKey, editingStore);

      if (response?.status !== 200) {
        alert(`Store ID ${editingStore} ${response.response.data.error}: ${response.response.config.url}`);
      } else {
        const concreteStore = new Store(response.data);
        setStoreName(concreteStore.name);
        setStoreAddress(concreteStore.address || "");
        setStoreAddress2(concreteStore.address2 || "");
        setStoreCity(concreteStore.city || "");
        setStoreState(concreteStore.state || "");
        setStorePostalCode(concreteStore.postal_code || "");
        setStoreCountry(concreteStore.country || "");
        setStoreId(concreteStore.id);
        setStoreLocale(concreteStore.locale);
        setStoreTimezone(concreteStore.timezone);
        setStoreProperties(concreteStore.properties);
        setOloStoreNumber(concreteStore.olo_store_number);

        if (concreteStore.properties.has_order_board) {
          setEnableOrderBoard(concreteStore.properties.has_order_board as boolean);
        }

        // convert the JSON object into a string for the text field
        const oloBrandDet = (concreteStore.olo_brand_details) ? JSON.stringify(concreteStore.olo_brand_details).replace('","', '",\r\n"') : '';

        if (oloBrandDet === '') {
          setDefaultOloBrandDetails();
        } else {
          setOloBrandDetails(oloBrandDet);
        }

        if (concreteStore.olo_store_number) {
          setEnableOlo(true);
        }

        if (concreteStore.name.length && concreteStore.locale.length && concreteStore.timezone.length && (concreteStore.city || '').length) {
          setIsFormValid(true);
        } else {
          setIsFormValid(false);
        }
      }
    };

    if (editingStore) {
      fetchStore();
      setHideId(false);
    } else {
      setDefaultOloBrandDetails();
      setHideId(true);
    }
  }, []);

  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px'
          }}>
          {storeName || 'New Store'}
        </div>
        <Button 
          sx={{
            borderRadius: '5px',
            width: 'auto',
            height: 'auto',
            padding: '0',
            minWidth: '0',
            '&:hover': {
              opacity: '.5',
            }
          }}
          onClick={() => {
            setDialog(false);
          }}>
          <CloseIcon
            sx={{
              padding: '0',
              fontSize: '2rem',
              color: '#121F35',
              width: '40px',
              height: '40px',
            }} />
        </Button>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '576px',
        }}>
        <Box
          id='add-store'
          sx={{
            display: 'flex',
            height: '100%',
            flexDirection: 'column'
          }}>
          <Box 
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row'
              }}>
              { !hideId &&
              <TextField
                sx={{
                  width: '47%',
                  marginTop: '10px',
                  marginBottom: '20px',
                  marginRight: '15px'
                }}
                disabled
                label='ID'
                value={storeId} />
              }

              <TextField
                error={hasStoreNameError}
                helperText={storeNameErrorText}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: '47%',
                  marginTop: '10px',
                  marginBottom: '20px',
                  marginLeft: '15px'
                }}
                required
                label='Name'
                value={storeName}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setStoreNameErrorText('Store Name is required');
                    setHasStoreNameError(true);
                  } else {
                    setStoreNameErrorText('');
                    setHasStoreNameError(false);
                  }
                }}
                onChange = {(e) => {
                  setStoreName(e.target.value);
                }} />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row'
              }}>
              <TextField
                inputProps={{ maxLength: 255 }}
                sx={{
                  width: '47%',
                  display: 'flex',
                  marginBottom: '20px',
                  marginRight: '15px'
                }}
                label='Address'
                value={storeAddress}
                onChange = {(e) => {
                  setStoreAddress(e.target.value);
                }} />

              <TextField
                inputProps={{ maxLength: 255 }}
                sx={{
                  width: '47%',
                  display: 'flex',
                  marginBottom: '20px',
                  marginLeft: '15px'
                }}
                label='Address 2'
                value={storeAddress2}
                onChange = {(e) => {
                  setStoreAddress2(e.target.value);
                }} />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row'
              }}>
              <Box
                sx={{
                  width: '47%',
                  marginRight: '15px',
                  marginBottom: '20px',
                  display: 'flex',
                  flexDirection: 'row'
                }}>
                <TextField
                  inputProps={{ maxLength: 255 }}
                  sx={{
                    width: '49%',
                    display: 'flex',
                    marginRight: '15px'
                  }}
                  label='City'
                  required
                  value={storeCity}
                  onChange = {(e) => {
                    setStoreCity(e.target.value);
                  }} />

                <TextField
                  inputProps={{ maxLength: 255 }}
                  sx={{
                    width: '49%',
                    display: 'flex'
                  }}
                  label='State'
                  value={storeState}
                  onChange = {(e) => {
                    setStoreState(e.target.value);
                  }} />
              </Box>

              <Box
                sx={{
                  width: '47%',
                  display: 'flex',
                  marginBottom: '20px',
                  marginLeft: '15px',
                  flexDirection: 'row'
                }}>
                <TextField
                  inputProps={{ maxLength: 255 }}
                  sx={{
                    width: '49%',
                    display: 'flex',
                    marginRight: '15px'
                  }}
                  label='Postal Code'
                  value={storePostalCode}
                  onChange = {(e) => {
                    setStorePostalCode(e.target.value);
                  }} />

                <TextField
                  inputProps={{ maxLength: 255 }}
                  sx={{
                    width: '49%',
                    display: 'flex'
                  }}
                  label='Country'
                  value={storeCountry}
                  onChange = {(e) => {
                    setStoreCountry(e.target.value);
                  }} />
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '48.3%',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginRight: '15px'
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginRight: '30px',
                      marginLeft: '15px',
                      width: '20%'
                    }}>
                    <Typography
                      sx={{

                      }}>Olo Enabled</Typography>

                    <Switch
                      sx={{
                        marginBottom: '15px'
                      }}
                      checked={enableOlo}
                      onChange={toggleOlo}></Switch>
                  </Box>

                  <TextField
                    error={hasOloStoreNumberError}
                    helperText={oloStoreNumberErrorText}
                    inputProps={{ maxLength: 24 }}
                    disabled={!enableOlo}
                    required={enableOlo}
                    sx={{
                      width: '75%',
                      display: 'flex'
                    }}
                    label='Olo Store Number'
                    value={oloStoreNumber}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setOloStoreNumberErrorText('Olo Store Number is required');
                        setHasOloStoreNumberError(true);
                      } else {
                        setOloStoreNumberErrorText('');
                        setHasOloStoreNumberError(false);
                      }
                    }}
                    onChange = {validateOloStoreNumber} />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginRight: '15px',
                    marginBottom: '15px'
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <Typography
                      sx={{
                        marginTop: '15px',
                        width: '27.4%',
                        marginLeft: '15px'
                      }}>TimeZone *</Typography>
                    <Select
                      error={hasTimezoneError}
                      sx={{
                        color: 'black',
                        height: '56px',
                        marginRight: '15px',
                        width: '452px'
                      }} 
                      value={storeTimezone}
                      required
                      onBlur = {(e) => {
                        if (!e.target.value) {
                          setTimezoneErrorText('Timezone is required');
                          setHasTimezoneError(true);
                        } else {
                          setTimezoneErrorText('');
                          setHasTimezoneError(false);
                        }
                      }}
                      onChange = {(e) => {
                        setStoreTimezone(e.target.value);
                      }}>
                      {timeZones.map((x, index) => (
                        <MenuItem 
                          key={index}
                          value={x}>
                          {x} 
                        </MenuItem>
                      ))}
                    </Select>
                    <Box
                      sx={{
                        marginLeft: '12px'
                      }}>
                        {hasTimezoneError ? (
                          <FormHelperText error>{timezoneErrorText}</FormHelperText>
                        ) : null}
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row'
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginRight: '30px',
                      marginLeft: '15px',
                      width: '49%'
                    }}>
                    <Typography
                      sx={{

                      }}>Store Has Order Board</Typography>

                    <Switch
                      sx={{
                        marginBottom: '15px'
                      }}
                      checked={enableOrderBoard}
                      onChange={(e) => {
                        setEnableOrderBoard(e.target.checked);
                      }}></Switch>
                  </Box>

                  <TextField
                    error={hasLocaleError}
                    helperText={localeErrorText}
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      width: '49%',
                      marginBottom: '20px',
                    }}
                    required
                    label='Locale'
                    value={storeLocale}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setLocaleErrorText('Locale is required');
                        setHasLocaleError(true);
                      } else {
                        setLocaleErrorText('');
                        setHasLocaleError(false);
                      }
                    }}
                    onChange = {(e) => {
                      setStoreLocale(e.target.value);
                    }} />
                </Box>
              </Box>

              <TextField
                label="Olo Brand Details"
                multiline
                disabled={!enableOlo}
                sx={{
                  width: '47%',
                  marginLeft: '15px'
                }}
                value={oloBrandDetails}
                onChange = {(e) => {
                  setOloBrandDetails(e.target.value);
                }} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row'
            }}>
            <Button
              disabled={!isFormValid}
              variant='contained'
              sx={{
                marginBottom: '10px',
                width: '100px',
                fontSize: '24px'
              }}
              onClick={async() => { await upsertEditedStore(); }}>
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Box>
  );
}

export default StoreDialog;
